import { useState, useRef, useCallback, useEffect, forwardRef } from 'react'
import { Input, Button, Dropdown, Typography, Spin, Empty, Space } from 'antd'
import { SearchOutlined, LoadingOutlined } from '@ant-design/icons'
import { restClient } from '@/Core'
import { searchResourceKey } from '@/Ship/Configs/SearchResourceConfig'
import { OpenResourceCardTask } from '@/Core/Tasks/Resources/OpenResourceCardTask'
import { GetResourceTask } from '@/Core/Tasks/Resources/GetResourceTask'
import { classNames } from '@/Ship/Helpers/classNames'

import styles from './Search.module.css'
import { groupSearchResults } from './groupSearchResults'
import { isSearchable, normalizeSearchQuery } from './searchQuery'
import { uniqueSearchResults } from './uniqueSearchResults'

import type { KeyboardEvent } from 'react'

/**
 * Інтерфейс для результату пошуку
 */
export interface ISearchResult {
    resource: string
    id: number
    title: string
    score: number
    /**
     * Ідентифікатор картки, яку відкриває клік.
     *
     * Колонка в індексі nullable, тож необов'язковий — і перед відкриттям
     * картки перевіряється.
     */
    uuid?: string
    payload?: Record<string, unknown>
}

export interface ISearchProps {
    /** Ширина поля, px. Ігнорується з `block`. */
    width?: number
    /** На всю ширину батька — мобільний вигляд (`SPEC-014` §1.4). */
    block?: boolean
    /** Підказка в полі */
    placeholder?: string
    /** Затримка перед запитом після вводу, мс */
    debounceDelay?: number
}

/** Підпис групи — назва ресурсу картки («Клієнти», «Контакти клієнта»…), як у легасі. */
const groupTitle = (backendResource: string): string => {
    const key = searchResourceKey(backendResource)

    return (key && GetResourceTask(key)?.title) || backendResource
}

/**
 * Глобальний пошук у шапці (`SPEC-014` §5.3).
 *
 * - запит — від 2 символів, через 500 мс після останнього вводу (як у
 *   легасі); Enter і кнопка шукають одразу;
 * - телефон у будь-якому записі зводиться до цифр (`normalizeSearchQuery`);
 * - видача — групами в порядку легасі, із заголовком-назвою ресурсу; назва
 *   запису — текстом, без HTML (D-35);
 * - клік відкриває картку, список закривається, поле очищується;
 * - відповідь на застарілий запит ігнорується: друкуючи швидко, людина не
 *   побачить видачу за попереднім словом поверх нової.
 *
 * Ключ картки — лише з `SEARCH_RESOURCES`: ключ прав контактів (`Client`) і
 * їхня пошукова назва (`ClientContact`) різні.
 */
export const Search = forwardRef<HTMLDivElement, ISearchProps>(
    ({ width = 240, block = false, placeholder = 'Що Ви шукаєте?', debounceDelay = 500 }, ref) => {
        const [searchValue, setSearchValue] = useState('')
        const [results, setResults] = useState<ISearchResult[]>([])
        const [isLoading, setIsLoading] = useState(false)
        const [isOpen, setIsOpen] = useState(false)
        const inputRef = useRef<HTMLDivElement | null>(null)
        const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
        // Номер останнього запиту: відповіді попередніх відкидаються
        const requestSeqRef = useRef(0)

        const clearTimer = () => {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current)
                debounceTimerRef.current = null
            }
        }

        const reset = () => {
            requestSeqRef.current += 1
            setResults([])
            setIsOpen(false)
            setIsLoading(false)
        }

        const performSearch = useCallback(async (raw: string) => {
            const query = normalizeSearchQuery(raw)

            if (!isSearchable(query)) {
                reset()
                return
            }

            const seq = ++requestSeqRef.current
            setIsLoading(true)

            try {
                const response = (await restClient.get('/services/search', {
                    searchParams: { q: query },
                })) as unknown

                if (seq !== requestSeqRef.current) return

                setResults(Array.isArray(response) ? uniqueSearchResults(response as ISearchResult[]) : [])
                setIsOpen(true)
            } catch (error) {
                if (seq !== requestSeqRef.current) return

                console.warn('Search: запит не вдався', error)
                setResults([])
                setIsOpen(false)
            } finally {
                if (seq === requestSeqRef.current) setIsLoading(false)
            }
        }, [])

        const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const value = e.target.value
            setSearchValue(value)
            clearTimer()

            if (!isSearchable(normalizeSearchQuery(value))) {
                reset()
                return
            }

            debounceTimerRef.current = setTimeout(() => {
                void performSearch(value)
            }, debounceDelay)
        }

        const searchNow = () => {
            clearTimer()

            if (searchValue.trim()) {
                void performSearch(searchValue)
            }
        }

        const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                searchNow()
            } else if (e.key === 'Escape') {
                setIsOpen(false)
            }
        }

        const handleFocus = () => {
            // Повертаємо список, якщо видача є для поточного запиту
            if (results.length > 0 && isSearchable(normalizeSearchQuery(searchValue))) {
                setIsOpen(true)
            }
        }

        const handleResultClick = (result: ISearchResult) => {
            const frontendKey = searchResourceKey(result.resource)

            if (!frontendKey || !result.uuid) {
                return
            }

            clearTimer()
            setSearchValue('')
            reset()

            OpenResourceCardTask({
                resource: frontendKey,
                primaryFieldValue: result.uuid,
                mode: 'view',
            })
        }

        // Закриття списку кліком поза ним; таймер — геть при демонтажі
        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
                    setIsOpen(false)
                }
            }

            document.addEventListener('mousedown', handleClickOutside)
            return () => {
                document.removeEventListener('mousedown', handleClickOutside)
                clearTimer()
            }
        }, [])

        const groups = groupSearchResults(results)

        const dropdownContent = (
            <div className={classNames(styles['searchResults'])} data-testid="global-search-results">
                {isLoading ? (
                    <div className={classNames(styles['loadingContainer'])}>
                        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
                    </div>
                ) : results.length > 0 ? (
                    <>
                        <div className={classNames(styles['resultsHeader'])}>Знайдено результатів: {results.length}</div>
                        {groups.map((group) => (
                            <section key={group.resource} className={classNames(styles['group'])} data-resource={group.resource}>
                                <div className={classNames(styles['groupHeader'])} role="heading" aria-level={3}>
                                    {groupTitle(group.resource)}
                                </div>
                                {group.items.map((item) => {
                                    const clickable = Boolean(searchResourceKey(item.resource) && item.uuid)

                                    return (
                                        <div
                                            key={`${item.resource}:${item.id}`}
                                            role="option"
                                            aria-selected={false}
                                            className={classNames(styles['searchResultItem'], clickable && styles['clickable'])}
                                            onMouseDown={(e) => {
                                                e.preventDefault()
                                                handleResultClick(item)
                                            }}
                                        >
                                            <Typography.Text className={classNames(styles['resultTitle'])}>{item.title}</Typography.Text>
                                        </div>
                                    )
                                })}
                            </section>
                        ))}
                    </>
                ) : (
                    <div className={classNames(styles['emptyState'])}>
                        <Empty description="Нічого не знайдено..." image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    </div>
                )}
            </div>
        )

        const setRefs = (node: HTMLDivElement | null) => {
            inputRef.current = node

            if (typeof ref === 'function') ref(node)
            else if (ref) ref.current = node
        }

        return (
            <div ref={setRefs} className={classNames(block && styles['block'])}>
                <Dropdown
                    open={isOpen}
                    popupRender={() => dropdownContent}
                    trigger={[]}
                    placement="bottomLeft"
                    classNames={{ root: classNames(styles['searchDropdown']) }}
                    destroyOnHidden
                >
                    <div>
                        <Space.Compact block={block}>
                            <Input
                                aria-label="Глобальний пошук"
                                placeholder={placeholder}
                                {...(block ? {} : { style: { width } })}
                                value={searchValue}
                                onChange={handleSearchChange}
                                onKeyDown={handleKeyDown}
                                onFocus={handleFocus}
                                autoComplete="off"
                            />
                            <Button aria-label="Шукати" icon={<SearchOutlined />} onClick={searchNow} loading={isLoading} />
                        </Space.Compact>
                    </div>
                </Dropdown>
            </div>
        )
    }
)

Search.displayName = 'Search'
